import { StartClientToCreativeNetworkingChatDocument, StartCreativeNetworkingChatDocument } from "@/gql/graphql"
import { useMutation } from "@apollo/client"
import { captureException } from "@sentry/nextjs"
import { channel } from "diagnostics_channel"
import { Channel, StreamChat } from "stream-chat"

type ChannelType = "creative_networking" | "client_to_creative_networking" | "client_job" | "creative_job"

export const useGetOrCreateChannel = (client: StreamChat<any> | null) => {
  const [startCreativeChat] = useMutation(StartCreativeNetworkingChatDocument)
  const [startClientChat] = useMutation(StartClientToCreativeNetworkingChatDocument)

  const getOrCreateChannel = async ({
    creativeId,
    userUuid,
    channelType,
    sessionUserIsClient,
  }: {
    creativeId: string
    userUuid: string
    channelType: ChannelType
    sessionUserIsClient: boolean
  }) => {
    if (!client) {
      throw new Error("Client not initialized")
    }

    try {
      const existingChannels = await client.queryChannels({
        members: [client.userID!, userUuid],
      })

      if (existingChannels.length > 0) {
        // FIXME: For now we need to filter on the frontend because stream timesout with a filter
        const channel = existingChannels.find((channel: Channel) => channel.type === channelType)
        if (channel) {
          return channel
        }
      }
    } catch (e) {
      // Timeout error
      captureException(e)
    }

    let cid
    if (channelType === "creative_networking" || channelType === "client_to_creative_networking") {
      const { data } = await (sessionUserIsClient
        ? startClientChat({ variables: { creativeId, message: "" } })
        : startCreativeChat({ variables: { creativeId, message: "" } }))
      cid = data?.startNetworkingChat?.channelCid
    } else if (channelType === "client_job" || channelType === "creative_job") {
      // TODO: Add job chat mutation
      throw new Error("Job chat not implemented")
    }

    if (!cid) {
      return client.channel(channelType, {
        members: [client.userID!, userUuid],
      })
    }
    const channels = await client.queryChannels({
      cid: { $eq: cid },
      members: { $in: [client.userID!] },
    })

    const newChannel = channels?.[0]

    return newChannel
  }

  return {
    getOrCreateChannel,
  }
}
